import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Search,
  Pencil,
  Trash2,
  Users,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Eye,
  EyeOff,
  Save,
  X,
  Calendar,
  Hash,
  Activity,
  BarChart3,
  FolderOpen,
  MessageSquare,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { tenantsApi, Tenant, TenantUser, CreateTenantInput, UpdateTenantInput, UpdateTenantUserInput, CreateTenantUserInput, IntegrationSetting } from '@/services/api/tenants';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { MODULE_METADATA, TenantModuleKey, DEFAULT_TENANT_MODULES } from '@/types/tenant';
import { globalKnowledgeService } from '@/services/api/global-knowledge';
import { formatDistanceToNow } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserCircle, Shield, KeyRound, MoreHorizontal, Phone, Briefcase, UserPlus, Globe, Zap, Link2, Unlink, CheckCircle, RefreshCw, Clock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface FormData extends CreateTenantInput {
  adminEmail: string;
  adminPassword: string;
  adminFirstName: string;
  adminLastName: string;
  sendWelcomeEmail: boolean;
}

const initialFormData: FormData = {
  name: '',
  slug: '',
  plan: 'starter',
  status: 'active',
  adminEmail: '',
  adminPassword: '',
  adminFirstName: '',
  adminLastName: '',
  sendWelcomeEmail: true,
};

// User form data for editing
interface UserFormData {
  firstName: string;
  lastName: string;
  role: 'tenant_admin' | 'manager' | 'user';
  status: 'active' | 'inactive' | 'suspended';
  phone: string;
  jobTitle: string;
}

const initialUserFormData: UserFormData = {
  firstName: '',
  lastName: '',
  role: 'user',
  status: 'active',
  phone: '',
  jobTitle: '',
};

// Create user form data
interface CreateUserFormData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'tenant_admin' | 'manager' | 'user';
  phone: string;
  jobTitle: string;
  sendWelcomeEmail: boolean;
}

const initialCreateUserFormData: CreateUserFormData = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  role: 'user',
  phone: '',
  jobTitle: '',
  sendWelcomeEmail: true,
};

// Component for managing tenant modules
function TenantModulesTab({ tenant }: { tenant: Tenant }) {
  const queryClient = useQueryClient();
  const [modules, setModules] = useState<Record<string, boolean>>(
    tenant.features?.modules || DEFAULT_TENANT_MODULES
  );

  useEffect(() => {
    setModules(tenant.features?.modules || DEFAULT_TENANT_MODULES);
  }, [tenant.id]);

  const toggleMutation = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      tenantsApi.updateModules(tenant.id, { [key]: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Module updated');
    },
    onError: (_error, variables) => {
      setModules((prev) => ({ ...prev, [variables.key]: !variables.enabled }));
      toast.error('Failed to update module');
    },
  });

  const handleToggle = (key: string, enabled: boolean) => {
    setModules((prev) => ({ ...prev, [key]: enabled }));
    toggleMutation.mutate({ key, enabled });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Feature Modules</CardTitle>
        <CardDescription>
          Enable or disable feature modules for this tenant. Changes take effect immediately.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {(Object.keys(MODULE_METADATA) as TenantModuleKey[]).map((key) => {
            const meta = MODULE_METADATA[key];
            const enabled = modules[key] === true;
            return (
              <div
                key={key}
                className="flex items-center justify-between rounded-lg border p-4"
              >
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">{meta.label}</Label>
                  <p className="text-xs text-muted-foreground">{meta.description}</p>
                </div>
                <Switch
                  checked={enabled}
                  onCheckedChange={(checked) => handleToggle(key, checked)}
                />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// Component for managing tenant domain access
function TenantDomainsTab({ tenant }: { tenant: Tenant }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['tenant-domain-access', tenant.id],
    queryFn: () => globalKnowledgeService.getTenantDomainAccess(tenant.id),
    enabled: !!tenant.id,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ domainId, hasAccess }: { domainId: string; hasAccess: boolean }) =>
      globalKnowledgeService.setTenantDomainAccess(tenant.id, domainId, hasAccess),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-domain-access', tenant.id] });
      toast.success('Domain access updated');
    },
    onError: () => {
      toast.error('Failed to update domain access');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Globe className="h-4 w-4" />
          Global Knowledge Domains
        </CardTitle>
        <CardDescription>
          Control which global knowledge domains this tenant can search. Enabled domains allow the tenant's users to query documents within those domains.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        ) : data?.domains && data.domains.length > 0 ? (
          <div className="space-y-4">
            {data.domains.map((item) => (
              <div
                key={item.domainId}
                className="flex items-center justify-between rounded-lg border p-4"
              >
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">{item.domainName}</Label>
                  <p className="text-xs text-muted-foreground">
                    {item.description || 'No description'}
                    {item.categoryCount > 0 && ` · ${item.categoryCount} categories`}
                  </p>
                </div>
                <Switch
                  checked={item.hasAccess}
                  onCheckedChange={(checked) =>
                    toggleMutation.mutate({ domainId: item.domainId, hasAccess: checked })
                  }
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Globe className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>No global knowledge domains configured yet.</p>
            <p className="text-sm">Create domains in the Global Knowledge Base section first.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Component for displaying tenant users
function TenantUsersTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [userSearch, setUserSearch] = useState('');
  const [resetPasswordUser, setResetPasswordUser] = useState<TenantUser | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<TenantUser | null>(null);
  const [userFormData, setUserFormData] = useState<UserFormData>(initialUserFormData);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [createUserFormData, setCreateUserFormData] = useState<CreateUserFormData>(initialCreateUserFormData);
  const [showCreatePassword, setShowCreatePassword] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tenant-users', tenantId, userSearch],
    queryFn: () => tenantsApi.getUsers(tenantId, {
      search: userSearch || undefined,
      limit: 50
    }),
    enabled: !!tenantId,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      tenantsApi.resetUserPassword(tenantId, userId, password),
    onSuccess: () => {
      toast.success('Password reset successfully', {
        description: `Password for ${resetPasswordUser?.email} has been updated.`,
      });
      setResetPasswordUser(null);
      setResetPassword('');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to reset password';
      toast.error('Failed to reset password', { description: message });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateTenantUserInput }) =>
      tenantsApi.updateUser(tenantId, userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
      toast.success('User updated successfully');
      setEditingUser(null);
      setUserFormData(initialUserFormData);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to update user';
      toast.error('Failed to update user', { description: message });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => tenantsApi.deleteUser(tenantId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
      toast.success('User deleted successfully');
      setDeletingUser(null);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to delete user';
      toast.error('Failed to delete user', { description: message });
      setDeletingUser(null);
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (data: CreateTenantUserInput) => tenantsApi.createUser(tenantId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
      const user = response.data;
      toast.success('User created successfully', {
        description: user.temporaryPassword
          ? `A welcome email with login instructions has been sent to ${user.email}`
          : `User ${user.email} has been created.`,
      });
      setIsCreatingUser(false);
      setCreateUserFormData(initialCreateUserFormData);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to create user';
      toast.error('Failed to create user', { description: message });
    },
  });

  const users = data?.data || [];

  const getRoleBadge = (role: TenantUser['role']) => {
    switch (role) {
      case 'super_admin':
        return <Badge variant="destructive">Super Admin</Badge>;
      case 'tenant_admin':
        return <Badge className="bg-purple-500">Admin</Badge>;
      case 'manager':
        return <Badge variant="secondary">Manager</Badge>;
      default:
        return <Badge variant="outline">User</Badge>;
    }
  };

  const getStatusBadge = (status: TenantUser['status']) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inactive</Badge>;
      case 'suspended':
        return <Badge variant="destructive">Suspended</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleResetPassword = (user: TenantUser) => {
    setResetPasswordUser(user);
  };

  const confirmResetPassword = () => {
    if (resetPasswordUser && resetPassword.length >= 8) {
      resetPasswordMutation.mutate({ userId: resetPasswordUser.id, password: resetPassword });
    }
  };

  const handleEditUser = (user: TenantUser) => {
    setEditingUser(user);
    setUserFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      role: user.role === 'super_admin' ? 'tenant_admin' : user.role,
      status: user.status === 'pending' ? 'active' : user.status,
      phone: user.phone || '',
      jobTitle: user.jobTitle || '',
    });
  };

  const handleSaveUser = () => {
    if (!editingUser) return;
    updateUserMutation.mutate({
      userId: editingUser.id,
      data: {
        firstName: userFormData.firstName || undefined,
        lastName: userFormData.lastName || undefined,
        role: userFormData.role,
        status: userFormData.status,
        phone: userFormData.phone || undefined,
        jobTitle: userFormData.jobTitle || undefined,
      },
    });
  };

  const handleCloseEditDialog = () => {
    setEditingUser(null);
    setUserFormData(initialUserFormData);
  };

  const handleOpenCreateUser = () => {
    setIsCreatingUser(true);
    setCreateUserFormData(initialCreateUserFormData);
    setShowCreatePassword(false);
  };

  const handleCloseCreateUser = () => {
    setIsCreatingUser(false);
    setCreateUserFormData(initialCreateUserFormData);
  };

  const handleCreateUser = () => {
    if (!createUserFormData.email) return;
    createUserMutation.mutate({
      email: createUserFormData.email,
      password: createUserFormData.password || undefined,
      firstName: createUserFormData.firstName || undefined,
      lastName: createUserFormData.lastName || undefined,
      role: createUserFormData.role,
      phone: createUserFormData.phone || undefined,
      jobTitle: createUserFormData.jobTitle || undefined,
      sendWelcomeEmail: createUserFormData.sendWelcomeEmail,
    });
  };

  const isEmailValid = (email: string) => {
    if (!email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  return (
    <div className="space-y-4">
      {/* Search and Add User */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search users..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={handleOpenCreateUser}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="p-8 text-center text-muted-foreground">
              Failed to load users.
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/30" />
              <h3 className="mt-4 text-lg font-semibold">No users yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                This tenant doesn't have any users.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <UserCircle className="h-8 w-8 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-medium">
                            {user.firstName || user.lastName
                              ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                              : user.email.split('@')[0]}
                          </p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>{getStatusBadge(user.status)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {user.lastLoginAt
                        ? formatDistanceToNow(new Date(user.lastLoginAt), { addSuffix: true })
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditUser(user)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                            <KeyRound className="mr-2 h-4 w-4" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeletingUser(user)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination info */}
      {data?.pagination && data.pagination.total > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          Showing {users.length} of {data.pagination.total} users
        </p>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && handleCloseEditDialog()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update information for {editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-firstName">First Name</Label>
                <Input
                  id="edit-firstName"
                  placeholder="John"
                  value={userFormData.firstName}
                  onChange={(e) =>
                    setUserFormData((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-lastName">Last Name</Label>
                <Input
                  id="edit-lastName"
                  placeholder="Smith"
                  value={userFormData.lastName}
                  onChange={(e) =>
                    setUserFormData((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email Address</Label>
              <Input
                value={editingUser?.email || ''}
                disabled
                className="bg-muted"
              />
              <p className="text-xs text-muted-foreground">
                Email address cannot be changed.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select
                  value={userFormData.role}
                  onValueChange={(v) =>
                    setUserFormData((prev) => ({
                      ...prev,
                      role: v as 'tenant_admin' | 'manager' | 'user',
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tenant_admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-status">Status</Label>
                <Select
                  value={userFormData.status}
                  onValueChange={(v) =>
                    setUserFormData((prev) => ({
                      ...prev,
                      status: v as 'active' | 'inactive' | 'suspended',
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-phone">Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="edit-phone"
                  placeholder="+1 (555) 123-4567"
                  value={userFormData.phone}
                  onChange={(e) =>
                    setUserFormData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-jobTitle">Job Title</Label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="edit-jobTitle"
                  placeholder="Software Engineer"
                  value={userFormData.jobTitle}
                  onChange={(e) =>
                    setUserFormData((prev) => ({ ...prev, jobTitle: e.target.value }))
                  }
                  className="pl-9"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveUser}
              disabled={updateUserMutation.isPending}
            >
              {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <AlertDialog open={!!resetPasswordUser} onOpenChange={(open) => {
        if (!open) {
          setResetPasswordUser(null);
          setResetPassword('');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password</AlertDialogTitle>
            <AlertDialogDescription>
              Set a new password for <strong>{resetPasswordUser?.email}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label htmlFor="resetPassword">New Password</Label>
            <Input
              id="resetPassword"
              type="password"
              placeholder="Minimum 8 characters"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              className={resetPassword && resetPassword.length < 8 ? 'border-destructive' : ''}
            />
            {resetPassword && resetPassword.length < 8 && (
              <p className="text-xs text-destructive mt-1">Password must be at least 8 characters</p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!resetPassword || resetPassword.length < 8 || resetPasswordMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                confirmResetPassword();
              }}
            >
              {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete User Confirmation Dialog */}
      <AlertDialog open={!!deletingUser} onOpenChange={(open) => !open && setDeletingUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the user{' '}
              <strong>{deletingUser?.email}</strong>?
              <br /><br />
              This action cannot be undone. The user will lose access to the system and all their data will be removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingUser && deleteUserMutation.mutate(deletingUser.id)}
              disabled={deleteUserMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create User Dialog */}
      <Dialog open={isCreatingUser} onOpenChange={(open) => !open && handleCloseCreateUser()}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user for this tenant. They will receive a welcome email with login instructions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-firstName">First Name</Label>
                <Input
                  id="create-firstName"
                  placeholder="John"
                  value={createUserFormData.firstName}
                  onChange={(e) =>
                    setCreateUserFormData((prev) => ({ ...prev, firstName: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-lastName">Last Name</Label>
                <Input
                  id="create-lastName"
                  placeholder="Smith"
                  value={createUserFormData.lastName}
                  onChange={(e) =>
                    setCreateUserFormData((prev) => ({ ...prev, lastName: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-email">Email Address *</Label>
              <Input
                id="create-email"
                type="email"
                placeholder="john@example.com"
                value={createUserFormData.email}
                onChange={(e) =>
                  setCreateUserFormData((prev) => ({ ...prev, email: e.target.value }))
                }
                className={
                  createUserFormData.email && !isEmailValid(createUserFormData.email)
                    ? 'border-destructive'
                    : ''
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-password">Password (Optional)</Label>
              <div className="relative">
                <Input
                  id="create-password"
                  type={showCreatePassword ? 'text' : 'password'}
                  placeholder="Leave empty to auto-generate"
                  value={createUserFormData.password}
                  onChange={(e) =>
                    setCreateUserFormData((prev) => ({ ...prev, password: e.target.value }))
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowCreatePassword(!showCreatePassword)}
                >
                  {showCreatePassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty to auto-generate a temporary password.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-role">Role</Label>
                <Select
                  value={createUserFormData.role}
                  onValueChange={(v) =>
                    setCreateUserFormData((prev) => ({
                      ...prev,
                      role: v as 'tenant_admin' | 'manager' | 'user',
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tenant_admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-phone">Phone Number</Label>
                <Input
                  id="create-phone"
                  placeholder="+1 (555) 123-4567"
                  value={createUserFormData.phone}
                  onChange={(e) =>
                    setCreateUserFormData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-jobTitle">Job Title</Label>
              <Input
                id="create-jobTitle"
                placeholder="Software Engineer"
                value={createUserFormData.jobTitle}
                onChange={(e) =>
                  setCreateUserFormData((prev) => ({ ...prev, jobTitle: e.target.value }))
                }
              />
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="create-sendWelcomeEmail"
                checked={createUserFormData.sendWelcomeEmail}
                onCheckedChange={(checked) =>
                  setCreateUserFormData((prev) => ({ ...prev, sendWelcomeEmail: checked === true }))
                }
              />
              <label htmlFor="create-sendWelcomeEmail" className="text-sm flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Send welcome email with login instructions
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseCreateUser}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={!isEmailValid(createUserFormData.email) || createUserMutation.isPending}
            >
              {createUserMutation.isPending ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function TenantsPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  // Fetch tenants
  const { data, isLoading, error } = useQuery({
    queryKey: ['tenants', searchQuery, statusFilter, planFilter],
    queryFn: async () => {
      const result = await tenantsApi.list({
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        plan: planFilter || undefined,
        limit: 50,
      });
      return result;
    },
    staleTime: 0,
    gcTime: 0,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateTenantInput) => tenantsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      const adminUser = response.data?.adminUser;
      toast.success('Tenant created successfully', {
        description: adminUser
          ? `Admin user ${adminUser.email} has been created.`
          : `${formData.name} is now ready to use.`,
      });
      setIsCreating(false);
      setFormData(initialFormData);
      // Select the newly created tenant
      if (response.data) {
        setSelectedTenant(response.data);
      }
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      const message = errorData?.error || errorData?.message || error?.message || 'Failed to create tenant';
      toast.error('Failed to create tenant', { description: message });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenantInput }) =>
      tenantsApi.update(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant updated successfully');
      setIsEditing(false);
      // Update selected tenant with new data
      if (response.data) {
        setSelectedTenant(response.data);
      }
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      const message = errorData?.error || errorData?.message || error?.message || 'Failed to update tenant';
      toast.error('Failed to update tenant', { description: message });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => tenantsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      const deletedName = selectedTenant?.name;
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
      toast.success('Tenant deleted', {
        description: deletedName ? `${deletedName} has been permanently deleted.` : undefined,
      });
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      const message = errorData?.error || errorData?.message || error?.message || 'Failed to delete tenant';
      toast.error('Failed to delete tenant', { description: message });
    },
  });

  // Update form when selecting a tenant for editing
  useEffect(() => {
    if (selectedTenant && isEditing) {
      setFormData({
        ...initialFormData,
        name: selectedTenant.name,
        slug: selectedTenant.slug,
        plan: selectedTenant.plan,
        status: selectedTenant.status,
      });
    }
  }, [selectedTenant, isEditing]);

  const handleSelectTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleCreateNew = () => {
    setSelectedTenant(null);
    setIsCreating(true);
    setIsEditing(false);
    setFormData(initialFormData);
    setShowPassword(false);
  };

  const handleEdit = () => {
    if (selectedTenant) {
      setFormData({
        ...initialFormData,
        name: selectedTenant.name,
        slug: selectedTenant.slug,
        plan: selectedTenant.plan,
        status: selectedTenant.status,
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    if (isCreating) {
      createMutation.mutate({
        name: formData.name,
        slug: formData.slug,
        plan: formData.plan,
        status: formData.status,
        adminEmail: formData.adminEmail || undefined,
        adminPassword: formData.adminPassword || undefined,
        adminFirstName: formData.adminFirstName || undefined,
        adminLastName: formData.adminLastName || undefined,
        sendWelcomeEmail: formData.sendWelcomeEmail,
      });
    } else if (isEditing && selectedTenant) {
      updateMutation.mutate({
        id: selectedTenant.id,
        data: {
          name: formData.name,
          slug: formData.slug,
          plan: formData.plan,
          status: formData.status,
        },
      });
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedTenant) {
      deleteMutation.mutate(selectedTenant.id);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const isEmailValid = (email: string) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const canSubmit = () => {
    if (!formData.name) return false;
    if (isCreating && formData.adminEmail) {
      if (!isEmailValid(formData.adminEmail)) return false;
    }
    return true;
  };

  const getStatusBadge = (status: Tenant['status']) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </Badge>
        );
      case 'suspended':
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Suspended
          </Badge>
        );
      case 'trial':
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" />
            Trial
          </Badge>
        );
    }
  };

  const getPlanBadge = (plan: Tenant['plan']) => {
    switch (plan) {
      case 'starter':
        return <Badge variant="outline">Starter</Badge>;
      case 'professional':
        return <Badge variant="secondary">Professional</Badge>;
      case 'enterprise':
        return (
          <Badge variant="default" className="bg-purple-500">
            Enterprise
          </Badge>
        );
    }
  };

  const tenants = Array.isArray(data?.data) ? data.data : [];

  return (
    <div className="h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tenants</h1>
          <p className="text-muted-foreground">
            Manage tenants on the platform
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          Add Tenant
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-4">
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-xl font-bold">{data?.pagination?.total || 0}</p>
            </div>
            <Building2 className="h-8 w-8 text-muted-foreground/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Active</p>
              <p className="text-xl font-bold text-green-600">
                {tenants.filter((t) => t.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Trial</p>
              <p className="text-xl font-bold text-yellow-600">
                {tenants.filter((t) => t.status === 'trial').length}
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Enterprise</p>
              <p className="text-xl font-bold text-purple-600">
                {tenants.filter((t) => t.plan === 'enterprise').length}
              </p>
            </div>
            <Building2 className="h-8 w-8 text-purple-500/30" />
          </div>
        </Card>
      </div>

      {/* Main Content - Master-Detail Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-4 h-[calc(100%-10rem)]">
        {/* Left Panel - Tenant List (40%) */}
        <Card className="lg:col-span-4 flex flex-col overflow-hidden">
          <CardHeader className="pb-3 flex-shrink-0">
            <CardTitle className="text-base">All Tenants</CardTitle>
            <div className="flex flex-col gap-2 pt-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search tenants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <div className="flex gap-2">
                <Select
                  value={statusFilter || 'all'}
                  onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={planFilter || 'all'}
                  onValueChange={(v) => setPlanFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              {isLoading ? (
                <div className="space-y-2 p-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : error ? (
                <div className="py-8 text-center text-muted-foreground">
                  Failed to load tenants.
                </div>
              ) : tenants.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No tenants found.
                </div>
              ) : (
                <div className="divide-y">
                  {tenants.map((tenant) => (
                    <div
                      key={tenant.id}
                      onClick={() => handleSelectTenant(tenant)}
                      className={cn(
                        'p-3 cursor-pointer hover:bg-muted/50 transition-colors',
                        selectedTenant?.id === tenant.id && 'bg-muted'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                            <Building2 className="h-4 w-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{tenant.name}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {tenant.slug}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {getStatusBadge(tenant.status)}
                          {getPlanBadge(tenant.plan)}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {tenant.user_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {tenant.document_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right Panel - Details/Edit (60%) */}
        <Card className="lg:col-span-6 flex flex-col overflow-hidden">
          {!selectedTenant && !isCreating ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <Building2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a Tenant</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Choose a tenant from the list to view details or create a new one.
                </p>
                <Button onClick={handleCreateNew}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create New Tenant
                </Button>
              </div>
            </div>
          ) : isCreating ? (
            // Create New Tenant Form
            <ScrollArea className="flex-1">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Create New Tenant</h2>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Tenant Details */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      Tenant Details
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Tenant Name *</Label>
                        <Input
                          id="name"
                          placeholder="Acme Corporation"
                          value={formData.name}
                          onChange={(e) => {
                            const name = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              name,
                              slug: generateSlug(name),
                            }));
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="slug">Slug</Label>
                        <Input
                          id="slug"
                          placeholder="acme-corporation"
                          value={formData.slug}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, slug: e.target.value }))
                          }
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="plan">Plan</Label>
                        <Select
                          value={formData.plan}
                          onValueChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              plan: v as 'starter' | 'professional' | 'enterprise',
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="starter">Starter</SelectItem>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: v as 'active' | 'suspended' | 'trial',
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Admin User Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Users className="h-4 w-4" />
                      Admin User (Optional)
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="adminFirstName">First Name</Label>
                        <Input
                          id="adminFirstName"
                          placeholder="John"
                          value={formData.adminFirstName}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, adminFirstName: e.target.value }))
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="adminLastName">Last Name</Label>
                        <Input
                          id="adminLastName"
                          placeholder="Smith"
                          value={formData.adminLastName}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, adminLastName: e.target.value }))
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="adminEmail">Email Address</Label>
                      <Input
                        id="adminEmail"
                        type="email"
                        placeholder="john@acme.com"
                        value={formData.adminEmail}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, adminEmail: e.target.value }))
                        }
                        className={
                          formData.adminEmail && !isEmailValid(formData.adminEmail)
                            ? 'border-destructive'
                            : ''
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="adminPassword">Password (Optional)</Label>
                      <div className="relative">
                        <Input
                          id="adminPassword"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Leave empty to auto-generate"
                          value={formData.adminPassword}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, adminPassword: e.target.value }))
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sendWelcomeEmail"
                        checked={formData.sendWelcomeEmail}
                        onCheckedChange={(checked) =>
                          setFormData((prev) => ({ ...prev, sendWelcomeEmail: checked === true }))
                        }
                      />
                      <label htmlFor="sendWelcomeEmail" className="text-sm flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Send welcome email with login instructions
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit() || createMutation.isPending}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Tenant'}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : selectedTenant && isEditing ? (
            // Edit Tenant Form
            <ScrollArea className="flex-1">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Edit Tenant</h2>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-name">Tenant Name *</Label>
                      <Input
                        id="edit-name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, name: e.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-slug">Slug</Label>
                      <Input
                        id="edit-slug"
                        value={formData.slug}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, slug: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-plan">Plan</Label>
                      <Select
                        value={formData.plan}
                        onValueChange={(v) =>
                          setFormData((prev) => ({
                            ...prev,
                            plan: v as 'starter' | 'professional' | 'enterprise',
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="starter">Starter</SelectItem>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(v) =>
                          setFormData((prev) => ({
                            ...prev,
                            status: v as 'active' | 'suspended' | 'trial',
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="trial">Trial</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!formData.name || updateMutation.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : selectedTenant ? (
            // View Tenant Details
            <Tabs defaultValue="overview" className="flex-1 flex flex-col min-h-0">
              <div className="p-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">{selectedTenant.name}</h2>
                      <p className="text-sm text-muted-foreground">{selectedTenant.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleEdit}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={handleDelete}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  {getStatusBadge(selectedTenant.status)}
                  {getPlanBadge(selectedTenant.plan)}
                </div>
              </div>

              <TabsList className="mx-4 mt-2 w-fit flex-shrink-0">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="modules">Modules</TabsTrigger>
                <TabsTrigger value="domains">Domains</TabsTrigger>
                <TabsTrigger value="usage">Usage & Stats</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1">
                <TabsContent value="overview" className="p-4 m-0">
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Users</span>
                      </div>
                      <p className="text-xl font-bold mt-1">{selectedTenant.user_count || 0}</p>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Documents</span>
                      </div>
                      <p className="text-xl font-bold mt-1">{selectedTenant.document_count || 0}</p>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Departments</span>
                      </div>
                      <p className="text-xl font-bold mt-1">-</p>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Conversations</span>
                      </div>
                      <p className="text-xl font-bold mt-1">-</p>
                    </Card>
                  </div>

                  {/* Details */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Tenant Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Tenant ID</p>
                          <p className="font-mono text-xs mt-1">{selectedTenant.id}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Slug</p>
                          <p className="font-mono mt-1">{selectedTenant.slug}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Plan</p>
                          <div className="mt-1">{getPlanBadge(selectedTenant.plan)}</div>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Status</p>
                          <div className="mt-1">{getStatusBadge(selectedTenant.status)}</div>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Created</p>
                          <p className="mt-1">
                            {new Date(selectedTenant.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Last Updated</p>
                          <p className="mt-1">
                            {new Date(selectedTenant.updated_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="users" className="p-4 m-0">
                  <TenantUsersTab tenantId={selectedTenant.id} />
                </TabsContent>

                <TabsContent value="modules" className="p-4 m-0">
                  <TenantModulesTab tenant={selectedTenant} />
                </TabsContent>

                <TabsContent value="domains" className="p-4 m-0">
                  <TenantDomainsTab tenant={selectedTenant} />
                </TabsContent>

                <TabsContent value="usage" className="p-4 m-0">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        Usage Statistics
                      </CardTitle>
                      <CardDescription>
                        Resource usage and activity metrics
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-8 text-muted-foreground">
                        <Activity className="h-12 w-12 mx-auto mb-4 opacity-30" />
                        <p>Usage statistics will be available soon.</p>
                        <p className="text-sm">Track API calls, storage, and user activity.</p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="settings" className="p-4 m-0">
                  <TenantIntegrationsTab tenantId={selectedTenant.id} />
                </TabsContent>
              </ScrollArea>
            </Tabs>
          ) : null}
        </Card>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => { setDeleteDialogOpen(open); if (!open) setDeleteConfirmText(''); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Permanently Delete Tenant
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 pt-1">
              <p>You are about to permanently delete <strong>{selectedTenant?.name}</strong>.</p>
              <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-xs space-y-1">
                <p className="font-semibold text-destructive">This will permanently remove:</p>
                <ul className="list-disc pl-4 space-y-0.5 text-muted-foreground">
                  <li>All users and their Cognito accounts</li>
                  <li>All clients, documents, and uploaded files (S3)</li>
                  <li>All chat conversations and AI history</li>
                  <li>All compliance, SMSF, and audit data</li>
                  <li>Xero connections and integrations</li>
                  <li>Subscription and billing records</li>
                </ul>
                <p className="font-semibold text-destructive pt-1">This action cannot be undone.</p>
              </div>
              <div className="pt-1">
                <p className="text-xs text-muted-foreground mb-1.5">
                  Type <strong className="text-foreground">{selectedTenant?.name}</strong> to confirm:
                </p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type tenant name to confirm..."
                  className="text-sm"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending || deleteConfirmText !== selectedTenant?.name}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Permanently'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ========================== Integration Settings Tab ==========================

const INTEGRATIONS_CONFIG: Record<string, {
  label: string;
  description: string;
  settings: { key: string; label: string; placeholder: string; type: 'text' | 'password' }[];
}> = {
  xero: {
    label: 'Xero',
    description: 'Accounting software integration',
    settings: [
      { key: 'client_id', label: 'Client ID', placeholder: 'Enter Xero Client ID', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'Enter Xero Client Secret', type: 'password' },
    ],
  },
  quickbooks: {
    label: 'QuickBooks',
    description: 'QuickBooks Online integration (coming soon)',
    settings: [
      { key: 'client_id', label: 'Client ID', placeholder: 'Enter QuickBooks Client ID', type: 'text' },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'Enter QuickBooks Client Secret', type: 'password' },
    ],
  },
  myob: {
    label: 'MYOB',
    description: 'MYOB accounting integration (coming soon)',
    settings: [
      { key: 'api_key', label: 'API Key', placeholder: 'Enter MYOB API Key', type: 'password' },
      { key: 'company_file', label: 'Company File URI', placeholder: 'Enter Company File URI', type: 'text' },
    ],
  },
  lodgeit: {
    label: 'LodgeiT',
    description: 'Tax lodgement integration (coming soon)',
    settings: [
      { key: 'api_key', label: 'API Key', placeholder: 'Enter LodgeiT API Key', type: 'password' },
    ],
  },
};

function TenantIntegrationsTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [showValues, setShowValues] = useState<Record<string, boolean>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [testingIntegration, setTestingIntegration] = useState<string | null>(null);

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['tenant-integrations', tenantId],
    queryFn: () => tenantsApi.getIntegrations(tenantId),
  });

  const allSettings = settingsData?.data || {};

  const saveMutation = useMutation({
    mutationFn: ({ integration, key, value }: { integration: string; key: string; value: string }) =>
      tenantsApi.setIntegration(tenantId, integration, key, value),
    onSuccess: (_, { integration, key }) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-integrations', tenantId] });
      const fieldKey = `${integration}.${key}`;
      setValues(prev => ({ ...prev, [fieldKey]: '' }));
      setSavingKey(null);
      toast.success(`${key} saved`);
    },
    onError: () => {
      setSavingKey(null);
      toast.error('Failed to save setting');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ integration, key }: { integration: string; key: string }) =>
      tenantsApi.deleteIntegration(tenantId, integration, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-integrations', tenantId] });
      toast.success('Setting removed');
    },
    onError: () => toast.error('Failed to remove setting'),
  });

  const testMutation = useMutation({
    mutationFn: (integration: string) => tenantsApi.testIntegration(tenantId, integration),
    onSuccess: (data, integration) => {
      setTestingIntegration(null);
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    },
    onError: () => {
      setTestingIntegration(null);
      toast.error('Connection test failed');
    },
  });

  const handleSave = (integration: string, key: string) => {
    const fieldKey = `${integration}.${key}`;
    const value = values[fieldKey];
    if (!value?.trim()) {
      toast.error('Value cannot be empty');
      return;
    }
    setSavingKey(fieldKey);
    saveMutation.mutate({ integration, key, value: value.trim() });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">API Integrations</h3>
        <p className="text-sm text-muted-foreground">
          Configure API credentials for third-party integrations
        </p>
      </div>

      {Object.entries(INTEGRATIONS_CONFIG).map(([integrationKey, config]) => {
        const integrationSettings = allSettings[integrationKey] || [];

        return (
          <Card key={integrationKey}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{config.label}</CardTitle>
              <CardDescription>{config.description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {config.settings.map(settingConfig => {
                const fieldKey = `${integrationKey}.${settingConfig.key}`;
                const setting = integrationSettings.find((s: IntegrationSetting) => s.key === settingConfig.key);
                const isConfigured = setting?.isConfigured ?? false;
                const inputValue = values[fieldKey] ?? '';
                const isPassword = settingConfig.type === 'password';
                const showValue = showValues[fieldKey] ?? false;

                return (
                  <div key={fieldKey} className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs font-medium">{settingConfig.label}</Label>
                      {isConfigured ? (
                        <Badge variant="default" className="text-[10px] px-1.5 py-0">Configured</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground">Not set</Badge>
                      )}
                    </div>
                    {isConfigured && setting?.maskedValue && (
                      <p className="text-[11px] text-muted-foreground font-mono">{setting.maskedValue}</p>
                    )}
                    <div className="flex gap-1.5">
                      <div className="relative flex-1">
                        <Input
                          type={isPassword && !showValue ? 'password' : 'text'}
                          placeholder={isConfigured ? 'Enter new value to update...' : settingConfig.placeholder}
                          value={inputValue}
                          onChange={e => setValues(prev => ({ ...prev, [fieldKey]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && inputValue && handleSave(integrationKey, settingConfig.key)}
                          className="h-8 text-sm"
                        />
                        {isPassword && (
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowValues(prev => ({ ...prev, [fieldKey]: !showValue }))}
                          >
                            {showValue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="h-8 px-2"
                        onClick={() => handleSave(integrationKey, settingConfig.key)}
                        disabled={!inputValue?.trim() || savingKey === fieldKey}
                      >
                        {savingKey === fieldKey ? (
                          <Clock className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Save className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      {isConfigured && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2"
                          onClick={() => deleteMutation.mutate({ integration: integrationKey, key: settingConfig.key })}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              <Separator className="my-2" />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    setTestingIntegration(integrationKey);
                    testMutation.mutate(integrationKey);
                  }}
                  disabled={testingIntegration === integrationKey || integrationSettings.filter((s: IntegrationSetting) => s.isConfigured).length === 0}
                >
                  {testingIntegration === integrationKey ? (
                    <Clock className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Zap className="mr-2 h-3.5 w-3.5" />
                  )}
                  Test Credentials
                </Button>
              </div>
              {integrationKey === 'xero' && (
                <XeroConnectionSection tenantId={tenantId} hasCredentials={integrationSettings.filter((s: IntegrationSetting) => s.isConfigured).length >= 2} />
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function XeroConnectionSection({ tenantId, hasCredentials }: { tenantId: string; hasCredentials: boolean }) {
  const queryClient = useQueryClient();

  const { data: xeroStatus, isLoading } = useQuery({
    queryKey: ['tenant-xero-status', tenantId],
    queryFn: () => tenantsApi.getXeroStatus(tenantId),
    retry: false,
  });

  const connectMutation = useMutation({
    mutationFn: () => tenantsApi.connectXero(tenantId, `/panel/tenants?xero_connected=true`),
    onSuccess: (data) => {
      window.location.href = data.authUrl;
    },
    onError: () => toast.error('Failed to initiate Xero connection'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => tenantsApi.disconnectXero(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-xero-status', tenantId] });
      toast.success('Xero disconnected');
    },
    onError: () => toast.error('Failed to disconnect Xero'),
  });

  if (isLoading) return null;

  return (
    <div className="space-y-2 pt-2">
      <Separator />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {xeroStatus?.connected ? (
            <>
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium text-green-700">OAuth Connected</span>
            </>
          ) : (
            <>
              <Link2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">OAuth not connected</span>
            </>
          )}
        </div>
        {xeroStatus?.connected ? (
          <div className="flex gap-1">
            <Button
              size="sm"
              className="h-7 text-xs"
              onClick={() => connectMutation.mutate()}
              disabled={connectMutation.isPending}
            >
              {connectMutation.isPending ? (
                <Clock className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="mr-1 h-3 w-3" />
              )}
              Reconnect
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="h-7 text-xs"
              onClick={() => disconnectMutation.mutate()}
              disabled={disconnectMutation.isPending}
            >
              <Unlink className="mr-1 h-3 w-3" />
              Disconnect
            </Button>
          </div>
        ) : (
          <Button
            size="sm"
            className="h-7 text-xs"
            onClick={() => connectMutation.mutate()}
            disabled={connectMutation.isPending || !hasCredentials}
          >
            {connectMutation.isPending ? (
              <Clock className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <Link2 className="mr-1 h-3 w-3" />
            )}
            Connect Xero Account
          </Button>
        )}
      </div>
      {xeroStatus?.connected && xeroStatus.connectedBy && (
        <p className="text-[11px] text-muted-foreground">
          Connected by: {xeroStatus.connectedBy}
        </p>
      )}
      {!hasCredentials && !xeroStatus?.connected && (
        <p className="text-[11px] text-muted-foreground">
          Set Client ID and Client Secret first, then connect.
        </p>
      )}
    </div>
  );
}
